import React, { useEffect, useState } from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Divider,
  Grid,
  Card,
  Backdrop,
  CircularProgress,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Alert,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LockIcon from '@mui/icons-material/Lock';
import DescriptionIcon from '@mui/icons-material/Description';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../api';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import MDDatePicker from 'components/MDDatePicker';

const GENDER_OPTIONS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
];

const MARITAL_STATUS_OPTIONS = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
];

const RELIGION_OPTIONS = [
  'HINDUISM',
  'ISLAM',
  'CHRISTIANITY',
  'SIKHISM',
  'BUDDHISM',
  'JAINISM',
  'OTHERS',
];

const DOC_TYPES = [
  { label: 'Aadhaar', key: 'aadhaar', showIssuedBy: false, showValidTill: false },
  { label: 'PAN', key: 'pan', showIssuedBy: false, showValidTill: false },
  { label: 'Voter Card', key: 'voter', showIssuedBy: false, showValidTill: false },
  { label: 'Passport', key: 'passport', showIssuedBy: true, showValidTill: true },
  { label: 'Driving License', key: 'driving-liciense', showIssuedBy: true, showValidTill: true },
];

const INDIA_COUNTRY_ID = 100;

// Calculate age from yyyy-mm-dd string
const calcAge = (dobStr) => {
  if (!dobStr) return '';
  const today = dayjs();
  const dob = dayjs(dobStr);
  if (!dob.isValid()) return '';
  return today.diff(dob, 'year');
};

const emptyAddress = {
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  state_id: null,
  postal_code: '',
  country_id: INDIA_COUNTRY_ID,
  address_type: 'residential',
};

function DonorRegistration() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);

  const [form, setForm] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    gender: '',
    marital_status: '',
    date_of_birth: '',
    age: '',
    email_address: '',
    religion: '',
    nationality: '',
    occupation: '',
    identification_mark: '',
    country_id: INDIA_COUNTRY_ID,
    remarks: '',
  });

  const [primaryPhone, setPrimaryPhone] = useState({ phone_number: '', phone_code: '91' });
  const [whatsappPhone, setWhatsappPhone] = useState({ phone_number: '', phone_code: '91' });

  const [addresses, setAddresses] = useState([{ ...emptyAddress }]);

  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [profileUploading, setProfileUploading] = useState(false);
  const [profileUploaded, setProfileUploaded] = useState(false);
  const [profileDonorId, setProfileDonorId] = useState(null);

  const [docFiles, setDocFiles] = useState({});
  const [docPreviews, setDocPreviews] = useState({});
  const [docUploading, setDocUploading] = useState({});
  const [docUploaded, setDocUploaded] = useState({});
  const [selectedDocTab, setSelectedDocTab] = useState(0);
  const [registered, setRegistered] = useState(false);
  const [registeredCode, setRegisteredCode] = useState('');

  useEffect(() => {
    api.master.getActiveCountries().then((res) => {
      if (res?.data?.countries?.length) {
        setCountries(res.data.countries);
        const def = res.data.countries.find((c) => c.is_default === 'Y' || c.is_default === true);
        if (def) {
          setForm((p) => ({
            ...p,
            country_id: def.id,
            nationality: def.nationality || def.country_name,
          }));
          setPrimaryPhone((p) => ({ ...p, phone_code: String(def.phonecode || '91') }));
          setWhatsappPhone((p) => ({ ...p, phone_code: String(def.phonecode || '91') }));
        }
      }
    });
    api.master.getActiveStates(INDIA_COUNTRY_ID).then((res) => {
      if (res?.data?.states?.length) setStates(res.data.states);
    });
  }, []);

  const handleCountryChange = (countryId) => {
    const selected = countries.find((c) => c.id === countryId);
    if (selected) {
      setForm((p) => ({
        ...p,
        country_id: countryId,
        nationality: selected.nationality || selected.country_name,
      }));
      const code = String(selected.phonecode || '');
      setPrimaryPhone((p) => ({ ...p, phone_code: code }));
      setWhatsappPhone((p) => ({ ...p, phone_code: code }));
    }
  };

  const handleFormChange = (field, value) => {
    const upper = typeof value === 'string' && field !== 'email_address' ? value.toUpperCase() : value;
    setForm((p) => ({ ...p, [field]: upper }));
  };

  const handleAddressChange = (idx, field, value) => {
    setAddresses((prev) =>
      prev.map((a, i) => {
        if (i !== idx) return a;
        if (field === 'state_id') {
          const st = states.find((s) => s.id === value);
          return { ...a, state_id: value, state: st ? st.name : a.state };
        }
        if (field === 'country_id') {
          return { ...a, country_id: value, state: '', state_id: null };
        }
        return { ...a, [field]: typeof value === 'string' ? value.toUpperCase() : value };
      })
    );
  };

  const handleAddAddress = () =>
    setAddresses((prev) => [...prev, { ...emptyAddress, country_id: form.country_id }]);
  const handleRemoveAddress = (idx) => setAddresses((prev) => prev.filter((_, i) => i !== idx));

  const isAgeValid = () => {
    if (!form.date_of_birth || !form.gender) return true;
    const age = Number(calcAge(form.date_of_birth));
    if (form.gender === 'F' && age < 18) return false;
    if (form.gender === 'M' && age < 21) return false;
    return true;
  };

  const validate = () => {
    if (!form.first_name) return 'First name is required';
    if (!form.gender) return 'Gender is required';
    if (!form.date_of_birth) return 'Date of birth is required';
    if (!isAgeValid()) {
      return form.gender === 'F'
        ? 'Female donors must be at least 18 years old'
        : 'Male donors must be at least 21 years old';
    }
    if (!form.country_id) return 'Country is required';
    if (!primaryPhone.phone_number) return 'Primary phone number is required';
    if (!addresses.length || !addresses[0].address_line_1) return 'At least one address is required';
    return null;
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      Swal.fire({ icon: 'warning', title: 'Validation', text: error });
      return;
    }

    const phones = [
      { country_code: primaryPhone.phone_code, phone_type: 'mobile', phone_number: primaryPhone.phone_number, is_default: 'Y' },
    ];
    if (whatsappPhone.phone_number) {
      phones.push({ country_code: whatsappPhone.phone_code, phone_type: 'whatsapp', phone_number: whatsappPhone.phone_number, is_default: 'N' });
    }

    const payload = {
      donor: {
        first_name: form.first_name,
        middle_name: form.middle_name || undefined,
        last_name: form.last_name || undefined,
        gender: form.gender,
        marital_status: form.marital_status || undefined,
        date_of_birth: form.date_of_birth,
        age: form.age ? Number(form.age) : undefined,
        email_address: form.email_address || undefined,
        religion: form.religion || undefined,
        nationality: form.nationality || undefined,
        occupation: form.occupation || undefined,
        identification_mark: form.identification_mark || undefined,
        country_id: form.country_id,
        remarks: form.remarks || undefined,
        phones,
        addresses,
      },
    };

    setLoading(true);
    try {
      const res = await api.contacts.registerDonor(payload);
      const donorData = res?.data?.donor || res?.data;
      const donorId = donorData?.id;
      setProfileDonorId(donorId);

      if (profileImage && donorId) {
        try {
          const fd = new FormData();
          fd.append('file', profileImage);
          fd.append('patient_id', String(donorId));
          await api.contacts.uploadDonorProfileImage(fd);
          setProfileUploaded(true);
        } catch (uploadErr) {
          console.error('Profile image upload failed:', uploadErr);
          Swal.fire({ icon: 'warning', title: 'Photo not uploaded', text: 'Donor was registered but profile photo upload failed. You can re-upload from the edit screen.', timer: 3000, showConfirmButton: false });
        }
      }

      setRegistered(true);
      setRegisteredCode(donorData?.external_code || '');
      // scroll to top so the success banner + document section is visible
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err?.response?.data?.message || 'Failed to register donor',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfileImage(file);
    setProfilePreview(URL.createObjectURL(file));
    setProfileUploaded(false);
  };

  const handleDocNumberChange = (docKey, value) => {
    setDocFiles((p) => ({ ...p, [docKey]: { ...(p[docKey] || {}), number: value.toUpperCase() } }));
  };
  const handleDocFileChange = (docKey, side, file) => {
    setDocFiles((p) => ({ ...p, [docKey]: { ...(p[docKey] || {}), [side]: file } }));
    if (file && file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setDocPreviews((p) => ({ ...p, [`${docKey}_${side}`]: url }));
    } else {
      setDocPreviews((p) => ({ ...p, [`${docKey}_${side}`]: null }));
    }
  };
  const handleDocUpload = async (doc) => {
    if (!profileDonorId) {
      Swal.fire({ icon: 'warning', title: 'Register donor first', text: 'Please register the donor before uploading documents.' });
      return;
    }
    const d = docFiles[doc.key] || {};
    if (!d.number) { Swal.fire({ icon: 'warning', title: 'Missing document number' }); return; }
    if (!d.front) { Swal.fire({ icon: 'warning', title: 'Missing front image' }); return; }

    setDocUploading((p) => ({ ...p, [doc.key]: true }));
    try {
      const fd = new FormData();
      fd.append('contact_id', profileDonorId);
      fd.append('document_number', d.number);
      fd.append('type', doc.key === 'voter' ? 'epic' : doc.key);
      fd.append('file1', d.front);
      if (d.back) fd.append('file2', d.back);
      fd.append('is_default', 'Y');
      fd.append('file_sub_type_1', `front_${doc.key}`);
      if (d.back) fd.append('file_sub_type_2', `back_${doc.key}`);
      if (doc.showIssuedBy && d.issued_by) fd.append('issued_by', d.issued_by);
      if (doc.showValidTill && d.valid_till) fd.append('valid_till', d.valid_till);

      await api.contacts.uploadDonorDocuments(fd);
      setDocUploaded((p) => ({ ...p, [doc.key]: true }));
      Swal.fire({ icon: 'success', title: `${doc.label} uploaded!`, timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Upload failed', text: err?.response?.data?.message || 'Failed to upload document' });
    } finally {
      setDocUploading((p) => ({ ...p, [doc.key]: false }));
    }
  };

  const quillModules = { toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']] };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop open={loading} sx={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <MDBox pt={4} pb={4} px={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <MDTypography variant="h4" fontWeight="bold">
            Donor Registration
          </MDTypography>
          {registered && (
            <Button variant="contained" color="success" size="large" onClick={() => navigate('/donor-list')}
              sx={{ color: '#fff', minWidth: 160 }}>
              Go to Donor List
            </Button>
          )}
        </Box>
        {registered && (
          <Alert severity="success" sx={{ mb: 3, fontSize: 14 }}>
            <strong>Donor registered successfully!</strong> Code: <strong>{registeredCode}</strong>. You can now upload documents below.
          </Alert>
        )}

        {/* Profile Picture */}
        <Card sx={{ p: 3, mb: 3 }}>
          <MDTypography variant="h6" fontWeight="medium" mb={2}>Profile Picture (Optional)</MDTypography>
          <Divider sx={{ mb: 2 }} />
          <Box display="flex" alignItems="center" gap={3}>
            <Box
              sx={{
                width: 120, height: 120, border: '2px dashed #00798E', borderRadius: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', background: '#f7f9fa', overflow: 'hidden',
              }}
              onClick={() => document.getElementById('donor-profile-upload').click()}
            >
              {profilePreview ? (
                <img src={profilePreview} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Box textAlign="center">
                  <PersonIcon sx={{ fontSize: 40, color: '#aaa' }} />
                  <Typography variant="caption" display="block" color="text.secondary">Click to select</Typography>
                </Box>
              )}
              <input id="donor-profile-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleProfileImageChange} />
            </Box>
            <Box>
              {profileImage && <Typography variant="body2" mb={1}>{profileImage.name}</Typography>}
              {profileUploaded && <Typography variant="body2" color="success.main">Profile picture will be uploaded on registration.</Typography>}
            </Box>
          </Box>
        </Card>

        {/* Personal Info */}
        <Card sx={{ p: 3, mb: 3 }}>
          <MDTypography variant="h6" fontWeight="medium" mb={2}>Personal Information</MDTypography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="First Name *" value={form.first_name}
                onChange={(e) => handleFormChange('first_name', e.target.value)}
                inputProps={{ style: { textTransform: 'uppercase' } }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Middle Name" value={form.middle_name}
                onChange={(e) => handleFormChange('middle_name', e.target.value)}
                inputProps={{ style: { textTransform: 'uppercase' } }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Last Name" value={form.last_name}
                onChange={(e) => handleFormChange('last_name', e.target.value)}
                inputProps={{ style: { textTransform: 'uppercase' } }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Gender *</InputLabel>
                <Select value={form.gender} label="Gender *"
                  onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value }))} sx={{ height: 45 }}>
                  {GENDER_OPTIONS.map((g) => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Marital Status</InputLabel>
                <Select value={form.marital_status} label="Marital Status"
                  onChange={(e) => setForm((p) => ({ ...p, marital_status: e.target.value }))} sx={{ height: 45 }}>
                  {MARITAL_STATUS_OPTIONS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* DOB datepicker */}
            <Grid item xs={12} sm={4}>
              <MDDatePicker
                label="Date of Birth *"
                value={form.date_of_birth}
                onChange={(date) => setForm((p) => ({ ...p, date_of_birth: date || '', age: date ? calcAge(date) : '' }))}
                disableFuture
                textFieldProps={{
                  fullWidth: true,
                  error: !!form.date_of_birth && !isAgeValid(),
                  helperText:
                    form.date_of_birth && !isAgeValid()
                      ? form.gender === 'F' ? 'Female donors must be 18+' : 'Male donors must be 21+'
                      : '',
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Age (Auto-calculated)" value={form.age}
                InputProps={{ readOnly: true }} sx={{ background: '#f5f5f5' }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Country *"
                value="India"
                InputProps={{ readOnly: true }}
                sx={{ background: '#f5f5f5' }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Nationality (auto-filled from country)" value={form.nationality}
                onChange={(e) => handleFormChange('nationality', e.target.value)}
                inputProps={{ style: { textTransform: 'uppercase' } }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Email Address" value={form.email_address}
                onChange={(e) => setForm((p) => ({ ...p, email_address: e.target.value }))} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Religion</InputLabel>
                <Select value={form.religion} label="Religion"
                  onChange={(e) => setForm((p) => ({ ...p, religion: e.target.value }))} sx={{ height: 45 }}>
                  {RELIGION_OPTIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Occupation" value={form.occupation}
                onChange={(e) => handleFormChange('occupation', e.target.value)}
                inputProps={{ style: { textTransform: 'uppercase' } }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Identification Mark" value={form.identification_mark}
                onChange={(e) => handleFormChange('identification_mark', e.target.value)}
                inputProps={{ style: { textTransform: 'uppercase' } }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" mb={0.5}>Remarks</Typography>
              <Box sx={{ '.ql-container': { minHeight: 80, borderRadius: '0 0 4px 4px' }, '.ql-toolbar': { borderRadius: '4px 4px 0 0' } }}>
                <ReactQuill theme="snow" value={form.remarks}
                  onChange={(val) => setForm((p) => ({ ...p, remarks: val }))}
                  modules={quillModules} />
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* Phone Numbers */}
        <Card sx={{ p: 3, mb: 3 }}>
          <MDTypography variant="h6" fontWeight="medium" mb={2}>Phone Numbers</MDTypography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            {/* Mobile — mandatory */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.primary" mb={1}>Mobile Number <span style={{ color: 'red' }}>*</span></Typography>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <TextField fullWidth size="small" label="Code" value={primaryPhone.phone_code}
                    onChange={(e) => setPrimaryPhone((p) => ({ ...p, phone_code: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start">+</InputAdornment> }} />
                </Grid>
                <Grid item xs={8}>
                  <TextField fullWidth size="small" label="Mobile Number" value={primaryPhone.phone_number}
                    onChange={(e) => setPrimaryPhone((p) => ({ ...p, phone_number: e.target.value }))} />
                </Grid>
              </Grid>
            </Grid>
            {/* WhatsApp — optional */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                WhatsApp Number <Typography component="span" variant="caption" color="text.disabled">(Optional)</Typography>
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <TextField fullWidth size="small" label="Code" value={whatsappPhone.phone_code}
                    onChange={(e) => setWhatsappPhone((p) => ({ ...p, phone_code: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start">+</InputAdornment> }} />
                </Grid>
                <Grid item xs={8}>
                  <TextField fullWidth size="small" label="WhatsApp Number" value={whatsappPhone.phone_number}
                    onChange={(e) => setWhatsappPhone((p) => ({ ...p, phone_number: e.target.value }))} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Card>

        {/* Addresses */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <MDTypography variant="h6" fontWeight="medium">Addresses</MDTypography>
            <Button startIcon={<AddCircleOutlineIcon />} size="small" onClick={handleAddAddress}>Add Address</Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {addresses.map((addr, idx) => (
            <Box key={idx} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2" color="text.secondary">Address {idx + 1}</Typography>
                {idx > 0 && (
                  <IconButton color="error" size="small" onClick={() => handleRemoveAddress(idx)}>
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                )}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Address Line 1 *" value={addr.address_line_1}
                    onChange={(e) => handleAddressChange(idx, 'address_line_1', e.target.value)}
                    inputProps={{ style: { textTransform: 'uppercase' } }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Address Line 2" value={addr.address_line_2}
                    onChange={(e) => handleAddressChange(idx, 'address_line_2', e.target.value)}
                    inputProps={{ style: { textTransform: 'uppercase' } }} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Country"
                    value="India"
                    InputProps={{ readOnly: true }}
                    sx={{ background: '#f5f5f5' }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  {addr.country_id === INDIA_COUNTRY_ID ? (
                    <FormControl fullWidth>
                      <InputLabel>State</InputLabel>
                      <Select value={addr.state_id || ''} label="State"
                        onChange={(e) => handleAddressChange(idx, 'state_id', e.target.value)} sx={{ height: 45 }}>
                        {states.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField fullWidth label="State" value={addr.state}
                      onChange={(e) => handleAddressChange(idx, 'state', e.target.value)}
                      inputProps={{ style: { textTransform: 'uppercase' } }} />
                  )}
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth label="City" value={addr.city}
                    onChange={(e) => handleAddressChange(idx, 'city', e.target.value)}
                    inputProps={{ style: { textTransform: 'uppercase' } }} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth label="Postal Code" value={addr.postal_code}
                    onChange={(e) => handleAddressChange(idx, 'postal_code', e.target.value)}
                    inputProps={{ style: { textTransform: 'uppercase' } }} />
                </Grid>
              </Grid>
            </Box>
          ))}
        </Card>

        {/* Documents — Post Registration Upload */}
        <Card
          sx={{
            p: 3, mb: 3,
            border: registered ? '2px solid #00798E' : '1px solid #e0e0e0',
            transition: 'border 0.3s',
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <DescriptionIcon sx={{ color: '#00798E', fontSize: 26 }} />
            <MDTypography variant="h6" fontWeight="medium">Document Upload</MDTypography>
            {!registered && (
              <Chip label="Register first" size="small" variant="outlined" sx={{ ml: 'auto', color: '#888', borderColor: '#ccc' }} />
            )}
            {registered && (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: '16px !important' }} />}
                label={`${Object.values(docUploaded).filter(Boolean).length} / ${DOC_TYPES.length} uploaded`}
                size="small"
                color={Object.values(docUploaded).filter(Boolean).length === DOC_TYPES.length ? 'success' : 'primary'}
                sx={{ ml: 'auto' }}
              />
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />

          {!registered ? (
            <Box
              textAlign="center"
              py={6}
              sx={{ background: '#fafafa', borderRadius: 2, border: '1px dashed #e0e0e0' }}
            >
              <LockIcon sx={{ fontSize: 52, color: '#bdbdbd', mb: 1 }} />
              <Typography variant="body1" color="text.secondary" fontWeight="medium">
                Register the donor to unlock document upload
              </Typography>
              <Typography variant="body2" color="text.disabled" mt={0.5}>
                Fill in the form above and click &ldquo;Register Donor&rdquo;
              </Typography>
            </Box>
          ) : (
            <>
              {/* Doc type tab selector */}
              <Tabs
                value={selectedDocTab}
                onChange={(_, v) => setSelectedDocTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 3, borderBottom: '1px solid #e0e0e0' }}
              >
                {DOC_TYPES.map((doc) => (
                  <Tab
                    key={doc.key}
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {docUploaded[doc.key]
                          ? <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 17 }} />
                          : <RadioButtonUncheckedIcon sx={{ color: '#bdbdbd', fontSize: 17 }} />}
                        <span>{doc.label}</span>
                      </Box>
                    }
                  />
                ))}
              </Tabs>

              {DOC_TYPES.map((doc, tabIdx) => {
                if (selectedDocTab !== tabIdx) return null;
                const d = docFiles[doc.key] || {};
                const isUploading = docUploading[doc.key];
                const isUploaded = docUploaded[doc.key];
                const frontPreview = docPreviews[`${doc.key}_front`];
                const backPreview = docPreviews[`${doc.key}_back`];

                return (
                  <Box key={doc.key}>
                    {isUploaded && (
                      <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                        <strong>{doc.label}</strong> uploaded successfully!
                      </Alert>
                    )}

                    <Grid container spacing={3}>
                      {/* Document number */}
                      <Grid item xs={12} sm={doc.showIssuedBy || doc.showValidTill ? 4 : 6}>
                        <TextField
                          fullWidth
                          label={`${doc.label} Number *`}
                          value={d.number || ''}
                          onChange={(e) => handleDocNumberChange(doc.key, e.target.value)}
                          inputProps={{ style: { textTransform: 'uppercase' } }}
                          disabled={isUploaded}
                        />
                      </Grid>

                      {doc.showIssuedBy && (
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label="Issued By"
                            value={d.issued_by || ''}
                            onChange={(e) =>
                              setDocFiles((p) => ({ ...p, [doc.key]: { ...(p[doc.key] || {}), issued_by: e.target.value.toUpperCase() } }))
                            }
                            inputProps={{ style: { textTransform: 'uppercase' } }}
                            disabled={isUploaded}
                          />
                        </Grid>
                      )}

                      {doc.showValidTill && (
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            type="date"
                            label="Valid Till"
                            InputLabelProps={{ shrink: true }}
                            value={d.valid_till || ''}
                            onChange={(e) =>
                              setDocFiles((p) => ({ ...p, [doc.key]: { ...(p[doc.key] || {}), valid_till: e.target.value } }))
                            }
                            disabled={isUploaded}
                          />
                        </Grid>
                      )}

                      {/* Front Side Upload */}
                      <Grid item xs={12} sm={6}>
                        <input
                          id={`doc-front-${doc.key}`}
                          type="file"
                          accept="image/*,application/pdf"
                          style={{ display: 'none' }}
                          onChange={(e) => handleDocFileChange(doc.key, 'front', e.target.files[0])}
                        />
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block" mb={0.5}>
                          Front Side <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <Box
                          onClick={() => !isUploaded && document.getElementById(`doc-front-${doc.key}`).click()}
                          sx={{
                            border: '2px dashed',
                            borderColor: d.front ? '#00798E' : '#e0e0e0',
                            borderRadius: 2,
                            p: 2,
                            minHeight: 150,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: isUploaded ? 'default' : 'pointer',
                            backgroundColor: d.front ? 'rgba(0,121,142,0.04)' : '#fafafa',
                            transition: 'all 0.2s',
                            overflow: 'hidden',
                            '&:hover': !isUploaded ? { borderColor: '#00798E', backgroundColor: 'rgba(0,121,142,0.06)' } : {},
                          }}
                        >
                          {frontPreview ? (
                            <Box textAlign="center">
                              <img
                                src={frontPreview}
                                alt="front"
                                style={{ maxHeight: 110, maxWidth: '100%', objectFit: 'contain', borderRadius: 4 }}
                              />
                              <Typography variant="caption" display="block" color="text.secondary" noWrap sx={{ mt: 0.5 }}>
                                {d.front?.name}
                              </Typography>
                            </Box>
                          ) : d.front ? (
                            <Box textAlign="center">
                              <DescriptionIcon sx={{ fontSize: 40, color: '#00798E' }} />
                              <Typography variant="caption" display="block" noWrap>{d.front.name}</Typography>
                            </Box>
                          ) : (
                            <Box textAlign="center">
                              <CloudUploadIcon sx={{ fontSize: 38, color: '#bdbdbd' }} />
                              <Typography variant="body2" color="text.secondary" mt={0.5} fontWeight="medium">
                                Click to upload front
                              </Typography>
                              <Typography variant="caption" color="text.disabled">
                                Image or PDF
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>

                      {/* Back Side Upload */}
                      <Grid item xs={12} sm={6}>
                        <input
                          id={`doc-back-${doc.key}`}
                          type="file"
                          accept="image/*,application/pdf"
                          style={{ display: 'none' }}
                          onChange={(e) => handleDocFileChange(doc.key, 'back', e.target.files[0])}
                        />
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block" mb={0.5}>
                          Back Side{' '}
                          <Typography component="span" variant="caption" color="text.disabled">(Optional)</Typography>
                        </Typography>
                        <Box
                          onClick={() => !isUploaded && document.getElementById(`doc-back-${doc.key}`).click()}
                          sx={{
                            border: '2px dashed',
                            borderColor: d.back ? '#00798E' : '#e0e0e0',
                            borderRadius: 2,
                            p: 2,
                            minHeight: 150,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: isUploaded ? 'default' : 'pointer',
                            backgroundColor: d.back ? 'rgba(0,121,142,0.04)' : '#fafafa',
                            transition: 'all 0.2s',
                            overflow: 'hidden',
                            '&:hover': !isUploaded ? { borderColor: '#00798E', backgroundColor: 'rgba(0,121,142,0.06)' } : {},
                          }}
                        >
                          {backPreview ? (
                            <Box textAlign="center">
                              <img
                                src={backPreview}
                                alt="back"
                                style={{ maxHeight: 110, maxWidth: '100%', objectFit: 'contain', borderRadius: 4 }}
                              />
                              <Typography variant="caption" display="block" color="text.secondary" noWrap sx={{ mt: 0.5 }}>
                                {d.back?.name}
                              </Typography>
                            </Box>
                          ) : d.back ? (
                            <Box textAlign="center">
                              <DescriptionIcon sx={{ fontSize: 40, color: '#00798E' }} />
                              <Typography variant="caption" display="block" noWrap>{d.back.name}</Typography>
                            </Box>
                          ) : (
                            <Box textAlign="center">
                              <CloudUploadIcon sx={{ fontSize: 38, color: '#bdbdbd' }} />
                              <Typography variant="body2" color="text.secondary" mt={0.5} fontWeight="medium">
                                Click to upload back
                              </Typography>
                              <Typography variant="caption" color="text.disabled">
                                Optional
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Upload action */}
                    <Box display="flex" justifyContent="flex-end" alignItems="center" mt={3}>
                      {isUploaded ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label={`${doc.label} uploaded`}
                          color="success"
                          variant="filled"
                          sx={{ px: 1 }}
                        />
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={
                            isUploading
                              ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                              : <CloudUploadIcon />
                          }
                          onClick={() => handleDocUpload(doc)}
                          disabled={isUploading}
                          sx={{ color: '#fff', minWidth: 180 }}
                        >
                          {isUploading ? 'Uploading…' : `Upload ${doc.label}`}
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </>
          )}
        </Card>

        {/* Submit */}
        {!registered && (
          <Box display="flex" justifyContent="flex-end">
            <button
              type="button"
              onClick={handleSubmit}
              style={{
                padding: '10px 24px',
                background: '#00798E',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                transition: 'background 0.3s ease',
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = '#006379')}
              onMouseOut={(e) => (e.currentTarget.style.background = '#00798E')}
            >
              <SaveIcon fontSize="small" />
              Register Donor
            </button>
          </Box>
        )}
        {registered && (
          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" color="success" size="large" onClick={() => navigate('/donor-list')}
              sx={{ minWidth: 180, color: '#fff' }}>
              Done — Go to Donor List
            </Button>
          </Box>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default DonorRegistration;
